import {useParams} from 'react-router-dom';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import {Box, IconButton, Tooltip, Typography} from '@mui/material';
import capitalize from 'lodash/capitalize';

import {useGetDepositDetails, useGetDepositLogs} from '@entities/transaction';
import {UserLink} from '@entities/user';
import {CardBox, CopyableText, CurrencyValue} from '@shared/components';
import {DATETIME_FORMAT} from '@shared/constants';
import {formatInTz} from '@shared/utils';
import {BaseWidgetLayout, Header} from '@widgets/common';
import {DetailsList, DetailsRow, ProviderLogsTable} from '@widgets/transaction';

import {COLOR__WHITE} from '@/theme/colors';

export const DepositFullInfoPage = () => {
  const {userId, id} = useParams<{userId: string; id: string}>();
  const params = {userId: userId ?? '', paymentId: id ?? ''};
  const isEnabled = !!userId && !!id;

  const {data, isPending, error} = useGetDepositDetails(params, {
    enabled: isEnabled,
  });

  const {
    data: logs,
    isFetching: areLogsFetching,
    error: logsError,
  } = useGetDepositLogs(params, {enabled: isEnabled});

  const rows: DetailsRow[] = data
    ? [
        {label: 'ID', value: <CopyableText value={data.id} />},
        {
          label: 'User ID',
          value: <UserLink userId={data.userId} />,
        },
        {label: 'Status', value: capitalize(data.status)},
        {label: 'Amount', value: <CurrencyValue value={data.amount} />},
        {label: 'Fee', value: <CurrencyValue value={data.fee} />},
        {
          label: 'Fee compensation',
          value: <CurrencyValue value={data.feeCompensation} />,
        },
        {label: 'Fiat amount', value: data.fiatAmount},
        {label: 'Fiat received', value: data.amountFiatReceived},
        {label: 'Rate snapshot', value: data.rateSnapshot},
        {
          // The provider falls back to the payment ID when it assigns no tracker
          label: 'Tracker ID',
          value: <CopyableText value={data.trackerId || data.id} />,
        },
        {
          label: 'Redirect URL',
          value: data.redirectUrl && (
            <Box display="flex" alignItems="center" gap={0.5}>
              {data.redirectUrl}
              <Tooltip title="Open in a new tab">
                <IconButton
                  size="small"
                  href={data.redirectUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <OpenInNewOutlinedIcon sx={{fontSize: 16}} />
                </IconButton>
              </Tooltip>
            </Box>
          ),
        },
        {label: 'Promo code', value: data.promoCode},
        {label: 'Promo percent', value: data.promoPercent},
        {
          label: 'Promo max bonus',
          value:
            data.promoMaxBonus === null ? null : (
              <CurrencyValue value={data.promoMaxBonus} />
            ),
        },
        {
          label: 'Created',
          value: data.created && formatInTz(data.created, DATETIME_FORMAT),
        },
        {
          label: 'Completed',
          value: data.completed && formatInTz(data.completed, DATETIME_FORMAT),
        },
        {label: 'Method', value: data.method?.name},
        {label: 'Method provider', value: data.method?.provider},
        {label: 'Method currency', value: data.method?.currency},
        {label: 'Method token', value: data.method?.tokenCode},
      ]
    : [];

  if (data?.status.toLowerCase() === 'failed') {
    if (data.failureCode) {
      rows.push({label: 'Failure code', value: data.failureCode});
    }
    if (data.failureDetail) {
      rows.push({label: 'Failure detail', value: data.failureDetail});
    }
  }

  return (
    <>
      <Header title={`Deposit ${id}`} mb={2} />

      <BaseWidgetLayout isPending={isPending} error={error}>
        <CardBox bgcolor={COLOR__WHITE}>
          <DetailsList rows={rows} />
        </CardBox>

        <CardBox bgcolor={COLOR__WHITE} mt={2}>
          <Typography component="div" fontWeight={500} mb={3}>
            Provider logs
          </Typography>

          <ProviderLogsTable
            logs={logs ?? []}
            isLoading={areLogsFetching}
            error={logsError}
          />
        </CardBox>
      </BaseWidgetLayout>
    </>
  );
};
